import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  getAllKPIs,
  deleteKPI,
  getAllUoMs,
  KPI_TYPE_LABELS,
} from "../services/kpiService";
import { useAppContext } from "../context/AppContext";
import { toast } from "../utils/toast";
import { getActionDropdownDirection } from "../utils/dropdownPosition";
import ConfirmModal from "../components/ui/ConfirmModal";
import "../styles/management-tables.css";
import "../styles/modal.css";
import "../styles/form.css";
import { resolveRoleName } from "../utils/auth";

const KPIManagementPage = () => {
  const { setActiveSection, setKpiId } = useAppContext();
  const [roleChecked, setRoleChecked] = useState(false);
  const [canAccess, setCanAccess] = useState(false);

  // State
  const [kpis, setKpis] = useState([]);
  const [uoms, setUoms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [typeFilter, setTypeFilter] = useState("All");

  // Pagination (client-side over the full, filtered list)
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);

  // Delete confirmation
  const [confirmId, setConfirmId] = useState(null);
  const [confirmBusy, setConfirmBusy] = useState(false);

  // Dropdown
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [dropdownDirection, setDropdownDirection] = useState("down");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        let user = null;
        try {
          const raw = sessionStorage.getItem("auth");
          const auth = raw ? JSON.parse(raw) : null;
          user = auth?.user || null;
        } catch {}
        const rn = user ? await resolveRoleName(user) : null;
        const norm = String(rn || "").toUpperCase();
        const allowed = norm === "SUPER_ADMIN" || norm === "SUPERADMIN";
        if (!cancelled) {
          setCanAccess(allowed);
          setRoleChecked(true);
        }
        if (!allowed) setActiveSection("dashboard");
      } catch {
        if (!cancelled) {
          setCanAccess(false);
          setRoleChecked(true);
        }
        setActiveSection("dashboard");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [setActiveSection]);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [kpiData, uomData] = await Promise.all([getAllKPIs(), getAllUoMs()]);
      setKpis(Array.isArray(kpiData) ? kpiData : []);
      setUoms(Array.isArray(uomData) ? uomData : []);
    } catch (err) {
      toast.error(err.message || "Failed to load KPIs");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Filtered KPIs
  const filteredKPIs = useMemo(() => {
    return kpis.filter((kpi) => {
      const matchesSearch =
        (kpi.kpiName || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
        (kpi.description || "")
          .toLowerCase()
          .includes(searchTerm.toLowerCase());
      const matchesType = typeFilter === "All" || kpi.kpiType === typeFilter;
      return matchesSearch && matchesType;
    });
  }, [kpis, searchTerm, typeFilter]);

  // Back to the first page whenever the filters or page size change
  useEffect(() => {
    setPage(1);
  }, [searchTerm, typeFilter, limit]);

  const totalRows = filteredKPIs.length;
  const totalPages = Math.max(1, Math.ceil(totalRows / limit));
  // Clamp so a delete on the last page doesn't leave an empty page
  const currentPage = Math.min(page, totalPages);
  const showingFrom = totalRows === 0 ? 0 : (currentPage - 1) * limit + 1;
  const showingTo = Math.min(currentPage * limit, totalRows);

  const pagedKPIs = useMemo(
    () => filteredKPIs.slice((currentPage - 1) * limit, currentPage * limit),
    [filteredKPIs, currentPage, limit],
  );

  const visiblePageItems = useMemo(() => {
    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    if (currentPage <= 2) {
      return [1, 2, 3, "ellipsis", totalPages];
    }

    if (currentPage === 3) {
      return [1, 2, 3, 4, "ellipsis", totalPages];
    }

    if (currentPage === totalPages - 2) {
      return [
        1,
        "ellipsis",
        totalPages - 3,
        totalPages - 2,
        totalPages - 1,
        totalPages,
      ];
    }

    if (currentPage >= totalPages - 1) {
      return [1, "ellipsis", totalPages - 2, totalPages - 1, totalPages];
    }

    return [
      1,
      "ellipsis",
      currentPage - 1,
      currentPage,
      currentPage + 1,
      "ellipsis",
      totalPages,
    ];
  }, [totalPages, currentPage]);

  // Get UoM display value
  const getUoMDisplay = (kpi) => {
    if (kpi.kpi_uom) {
      return kpi.kpi_uom.uomValue || "-";
    }
    const uom = uoms.find((u) => u.id === kpi.unitOfMeasure);
    return uom ? uom.uomValue : "-";
  };

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (!e.target.closest(".actions-menu-wrapper")) {
        setActiveDropdown(null);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Close dropdown on Escape
  useEffect(() => {
    const handler = (e) => {
      if (e.key === "Escape") setActiveDropdown(null);
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, []);

  const handleEdit = (kpi) => {
    try {
      localStorage.setItem("activeKpiId", String(kpi.id));
    } catch (_) {}
    setKpiId(kpi.id);
    setActiveSection("kpi-edit", { kpiId: kpi.id });
  };

  const handleDelete = async () => {
    if (!confirmId) return;
    setConfirmBusy(true);
    try {
      await deleteKPI(confirmId);
      toast.success("KPI deleted successfully");
      await loadData();
    } catch (err) {
      toast.error(err.message || "Failed to delete KPI");
    } finally {
      setConfirmBusy(false);
      setConfirmId(null);
    }
  };

  const confirmKpiName = kpis.find((k) => k.id === confirmId)?.kpiName || "";

  if (!roleChecked) {
    return (
      <div className="page-container">
        <div className="page-content">
          <div className="no-results">Loading...</div>
        </div>
      </div>
    );
  }
  if (!canAccess) {
    return (
      <div className="page-container">
        <div className="page-content">
          <div className="no-results">Access denied</div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="page-header">
        <div className="header-content">
          <div>
            <h2 className="page-title">KPI Management</h2>
            <p className="page-subtitle">
              Create and manage Key Performance Indicators for your processes
            </p>
          </div>
          <div className="roles-toolbar">
            <div className="roles-search">
              <input
                className="search-input"
                type="text"
                placeholder="Search KPIs..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <button className="secondary-btn sm" onClick={() => setSearchTerm("")}>
                Clear
              </button>
            </div>
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="filter-select"
            >
              <option value="All">All Types</option>
              {Object.entries(KPI_TYPE_LABELS).map(([key, label]) => (
                <option key={key} value={key}>{label}</option>
              ))}
            </select>
            <button
              className="secondary-btn"
              onClick={() => setActiveSection("uom-management")}
            >
              Manage UoMs
            </button>
            <button
              className="secondary-btn"
              onClick={() => setActiveSection("kpi-attribute-management")}
            >
              Manage Attributes
            </button>
            <button
              className="primary-btn"
              onClick={() => setActiveSection("kpi-create")}
            >
              + Create KPI
            </button>
          </div>
        </div>
      </div>

      <div className="page-content">
        {!loading && filteredKPIs.length > 0 && (
          <div className="kpi-pagination-top">
            <label htmlFor="kpi-limit" className="kpi-pagination-label">
              Rows per page:
            </label>
            <select
              id="kpi-limit"
              value={limit}
              onChange={(e) => setLimit(Number(e.target.value) || 10)}
            >
              {[5, 10, 20, 50].map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </select>
          </div>
        )}
        {loading ? (
          <div className="no-results">Loading KPIs...</div>
        ) : (
          <div className="roles-table kpi-management-table">
            {filteredKPIs.length === 0 ? (
              <div className="no-results">
                {kpis.length === 0 ? (
                  <>
                    <p>No KPIs yet.</p>
                    <button className="primary-btn" onClick={() => setActiveSection("kpi-create")} style={{ marginTop: 8 }}>
                      Create your first KPI
                    </button>
                  </>
                ) : (
                  "No KPIs match your search."
                )}
              </div>
            ) : (
              <table className="kpi-management-grid">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>KPI Name</th>
                    <th>Description</th>
                    <th>Unit of Measure</th>
                    <th>Type</th>
                    <th className="actions-column">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {pagedKPIs.map((kpi) => (
                    <tr key={kpi.id}>
                      <td>{kpi.id}</td>
                      <td className="kpi-name-cell">{kpi.kpiName}</td>
                      <td className="description-cell">
                        {kpi.description || <span className="empty-text">-</span>}
                      </td>
                      <td className="badge-cell">
                        <span className="uom-badge">{getUoMDisplay(kpi)}</span>
                      </td>
                      <td className="badge-cell">
                        <span className={`type-badge type-${kpi.kpiType?.toLowerCase()}`}>
                          {KPI_TYPE_LABELS[kpi.kpiType] || kpi.kpiType}
                        </span>
                      </td>
                      <td className="actions-column">
                        <div
                          className="actions-menu-wrapper"
                          style={{ position: "relative", display: "inline-block" }}
                        >
                          <button
                            className="action-menu-btn"
                            aria-haspopup="true"
                            aria-expanded={activeDropdown === kpi.id}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (activeDropdown === kpi.id) {
                                setActiveDropdown(null);
                              } else {
                                setDropdownDirection(getActionDropdownDirection(e.currentTarget));
                                setActiveDropdown(kpi.id);
                              }
                            }}
                          >
                            Actions ▼
                          </button>
                          {activeDropdown === kpi.id && (
                            <div
                              className={`action-dropdown-menu${
                                dropdownDirection === "up" ? " dropdown-up" : ""
                              }`}
                              role="menu"
                            >
                              <button
                                className="action-menu-item"
                                role="menuitem"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setActiveDropdown(null);
                                  handleEdit(kpi);
                                }}
                              >
                                Edit
                              </button>
                              <button
                                className="action-menu-item danger"
                                role="menuitem"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setActiveDropdown(null);
                                  setConfirmId(kpi.id);
                                }}
                              >
                                Delete
                              </button>
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
        {!loading && filteredKPIs.length > 0 && (
          <div className="kpi-pagination-bottom">
            <div className="kpi-pagination-summary">
              Showing {showingFrom} to {showingTo} of {totalRows} entries
            </div>
            <div className="kpi-pagination-actions">
              <button
                className="secondary-btn sm"
                onClick={() => setPage(Math.max(1, currentPage - 1))}
                disabled={currentPage <= 1}
              >
                Previous
              </button>
              {visiblePageItems.map((item, index) =>
                item === "ellipsis" ?
                  <span
                    key={`ellipsis-${index}`}
                    className="secondary-btn sm"
                    aria-hidden="true"
                  >
                    ...
                  </span>
                : <button
                    key={item}
                    className={
                      item === currentPage ? "primary-btn sm" : "secondary-btn sm"
                    }
                    onClick={() => setPage(item)}
                  >
                    {item}
                  </button>,
              )}
              <button
                className="secondary-btn sm"
                onClick={() => setPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage >= totalPages}
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      <ConfirmModal
        open={!!confirmId}
        title="Delete KPI"
        message={`Are you sure you want to delete "${confirmKpiName}"? This action cannot be undone.`}
        confirmLabel={confirmBusy ? "Deleting..." : "Delete"}
        cancelLabel="Cancel"
        isDanger
        onConfirm={handleDelete}
        onCancel={() => setConfirmId(null)}
        busy={confirmBusy}
      />
    </div>
  );
};

export default KPIManagementPage;

